// 공정 캘린더 「그날 환자 모달」 — 치과(ClinicProcessCalendar)·기공소(ProductionCalendar) 공용 (2026-09-29 대표 지시)
//  날짜(칸)·건 줄을 누르면 그날 도착 예정(납기) 환자를 카드로 나열: 환자명 · 상대 업체명 · 재제작 배지 · 치식 줄 · 공정 label/detail ·
//  갱신 시각 · 디자인 완료 칩 · [3D 디자인 확인] · 납기 · 특이사항 · [의뢰서 열기].
//  상태 변경 쓰기는 이 파일에 없다 — 기공소 쪽은 actionsOf(entry) 렌더 프롭으로 기존 보드 핸들러 버튼을 끼워 넣는다(치과는 버튼 0개).
//  모달: 스크롤 · Esc · 바깥 클릭 닫기 · 폰 전체화면 · 열릴 때 닫기 버튼 포커스 · 뒤 화면 스크롤 잠금.
import React, { useEffect, useRef, useState } from 'react';
import { remakeLabelOf, remakeBadgeColors } from '../utils/remakeKind';

const WEEK_KO = ['일', '월', '화', '수', '목', '금', '토'];

const toDate = (t) => {
    if (!t) return null;
    if (t.toDate) return t.toDate();
    if (t instanceof Date) return t;
    const d = new Date(t);
    return isNaN(d) ? null : d;
};
const fmtTime = (t) => {
    const d = toDate(t);
    if (!d) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
// 납기(도착 예정) — dueDate → deliveryDate 폴백 (ClinicProcessCalendar arriveOf와 동일)
export const dueOf = (o) => toDate(o?.dueDate) || toDate(o?.deliveryDate);

// 치식 차트 순서(ViewOrder와 동일) — 상악 우→좌, 하악 좌→우
const CHART_ORDER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28, 48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];
const chartSort = (teeth) => [...teeth].sort((a, b) => {
    const ia = CHART_ORDER.indexOf(a), ib = CHART_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
});
// 그룹의 teeth — 배열([14,15]) 또는 구건 문자열("14-16") 둘 다 받는다
const teethArr = (g) => {
    const t = g?.teeth ?? g;
    if (typeof t === 'number') return isNaN(t) ? [] : [t];
    if (Array.isArray(t)) return t.map(Number).filter(n => !isNaN(n));
    if (typeof t === 'string') return t.split(/[-,\s]+/).map(Number).filter(n => !isNaN(n));
    return [];
};

/**
 * 의뢰서 → 치아별 문자열 배열.
 *  "#14 지르코니아 크라운 · A3" / "#15 임플란트 Osstem TS Regular" / "#16 폰틱" / "브릿지 14-16 지르코니아 크라운"
 * 처리 필드: items[] > teethDetails(객체맵 또는 배열) > prosthesisGroups 순으로 치아별 보철·재료·쉐이드를 모으고,
 *  bridgeGroups(브릿지 한 줄)·implantGroups(브랜드)·ponticGroups(폰틱) 를 덧붙인다. 아무것도 없으면 selectedTeeth만 "#14 #15".
 */
export function teethLinesOf(o) {
    if (!o) return [];
    const per = {};   // tooth -> { prosthesis, material, shade, implant, isPontic }
    const touch = (t) => { const n = Number(t); if (isNaN(n)) return null; if (!per[n]) per[n] = {}; return per[n]; };
    const fill = (t, d) => {
        const p = touch(t); if (!p || !d) return;
        if (d.prosthesis && !p.prosthesis) p.prosthesis = d.prosthesis;
        if (d.material && !p.material) p.material = d.material;
        if (d.shade && !p.shade) p.shade = d.shade;
        if (d.implant && d.implant !== '없음' && !p.implant) p.implant = d.implant;
        if (d.isPontic) p.isPontic = true;
    };
    (o.items || []).filter(Boolean).forEach(it => fill(it.toothNumber ?? it.tooth, it));
    const td = o.teethDetails;
    if (Array.isArray(td)) td.filter(Boolean).forEach(d => fill(d.tooth ?? d.toothNumber, d));
    else if (td && typeof td === 'object') Object.entries(td).forEach(([t, d]) => fill(t, d));
    (o.prosthesisGroups || []).forEach(g => teethArr(g).forEach(t => fill(t, { prosthesis: g.prosthesis, material: g.material })));
    (o.implantGroups || []).forEach(g => teethArr(g).forEach(t => fill(t, { implant: g.brand || '임플란트' })));
    (o.ponticGroups || []).forEach(g => teethArr(g).forEach(t => fill(t, { isPontic: true })));

    // 브릿지는 2치 이상만 한 줄로. 1치짜리 그룹은 브릿지가 아니므로 개별 치아로 흡수(치아가 사라지지 않게)
    const bridges = [];
    (o.bridgeGroups || []).filter(Boolean).forEach(g => {
        const b = teethArr(g);
        if (b.length >= 2) bridges.push(b);
        else b.forEach(t => fill(t, { prosthesis: g?.prosthesis, material: g?.material }));
    });
    const inBridge = new Set(bridges.flat());

    const descOf = (p) => {
        const parts = [];
        if (p.implant) parts.push('임플란트 ' + p.implant);
        if (p.isPontic) parts.push('폰틱');
        const pm = [p.prosthesis, p.material].filter(Boolean).join(' ');
        if (pm) parts.push(pm);
        if (p.shade) parts.push(p.shade);
        return parts.join(' · ');
    };

    const lines = [];
    bridges.forEach(b => {
        const s = chartSort(b);
        const ps = s.map(t => per[t] || {});
        const first = ps.find(p => p.prosthesis || p.material) || {};
        const pm = [first.prosthesis, first.material].filter(Boolean).join(' ');
        const shade = [...new Set(ps.map(p => p.shade).filter(Boolean))].join(', ');
        const pon = s.filter(t => per[t]?.isPontic);
        let line = `브릿지 ${s[0]}-${s[s.length - 1]}`;
        if (pm) line += ' ' + pm;
        if (shade) line += ' · ' + shade;
        if (pon.length) line += ` (폰틱 ${pon.join(', ')})`;
        lines.push(line);
    });
    chartSort(Object.keys(per).map(Number)).forEach(t => {
        if (inBridge.has(t)) return;
        const d = descOf(per[t]);
        lines.push(d ? `#${t} ${d}` : `#${t}`);
    });
    if (lines.length === 0 && Array.isArray(o.selectedTeeth) && o.selectedTeeth.length) {
        lines.push(chartSort(o.selectedTeeth.map(Number)).map(t => '#' + t).join(' '));
    }
    return lines;
}

/**
 * @param {object} props
 *  date          Date — 모달 제목의 날짜. null이면 렌더하지 않는다
 *  entries       [{ key, o, st:{ label, detail }, color, done }] — o=workOrders 문서, st=공정 문구(치과=clinicStageOf, 기공소=보드 열 라벨)
 *  partnerNameOf (o) => 상대 업체명 (치과 화면=기공소명, 기공소 화면=치과명)
 *  dueLabel      납기 줄 이름 — 치과 '도착 예정' / 기공소 '납기'
 *  onClose()     닫기
 *  onOpen(o)     [의뢰서 열기]
 *  onPreview(o)  [3D 디자인 확인] — 없으면 버튼 미표시
 *  actionsOf(entry) → node|null  카드 하단 추가 조작(기공소: 단계 이동 버튼·밀링 체크). 없으면 무접촉(버튼 0개)
 *  foot          모달 하단 안내 한 줄(선택)
 *  escDisabled   true면 Esc 무시 — 위에 3D 뷰어(StlViewer)가 열려 있을 때 부모가 넘긴다(Esc가 뷰어 대신 이 모달을 닫지 않게, 2026-09-29 검토 P2-2)
 *  hideDesignChip true면 보조 '디자인완료' 칩 숨김 — 기공소 3단계(2026-10-12, ProductionCalendar mode 'lab')
 *  simple        true면 치과 「기공 캘린더」 모드 — 공정 행은 st.label(2026-10-12 3단계: 의뢰서 확인/디자인 완료/포장 완료/도착 예정일 지남, 확인 전 '-')만,
 *                보조 '디자인완료' 칩 숨김. 기공소(ProductionCalendar)는 미지정=기존 그대로 (구 2026-10-07 치과 2상태 모드를 대체)
 */
function ProcessDayModal({ date, entries, partnerNameOf, dueLabel = '도착 예정', onClose, onOpen, onPreview, actionsOf, foot, escDisabled = false, simple = false, hideDesignChip = false }) {
    const closeBtnRef = useRef(null);
    // 최신 onClose·escDisabled를 ref로 보관 — 부모가 인라인 함수를 넘겨도 이펙트가 매 렌더 재실행되지 않게 (2026-09-29 검토 P2-1)
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const escDisabledRef = useRef(escDisabled);
    escDisabledRef.current = escDisabled;
    const focusedOnceRef = useRef(false);         // 닫기 버튼 포커스는 열릴 때 1회만
    const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
    useEffect(() => {
        const onR = () => setVw(window.innerWidth);
        window.addEventListener('resize', onR);
        return () => window.removeEventListener('resize', onR);
    }, []);
    const phone = vw < 640;

    // Esc 닫기 + 열릴 때 닫기 버튼 포커스(1회) + 뒤 화면 스크롤 잠금(닫힐 때 복원) — 열림/닫힘(date 유무)에만 반응
    const open = !!date;
    useEffect(() => {
        if (!open) { focusedOnceRef.current = false; return undefined; }
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (escDisabledRef.current) return;   // 3D 뷰어가 위에 떠 있으면 Esc는 뷰어 몫
            if (onCloseRef.current) onCloseRef.current();
        };
        window.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        if (!focusedOnceRef.current && closeBtnRef.current) {
            closeBtnRef.current.focus();
            focusedOnceRef.current = true;
        }
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open]);

    if (!date) return null;
    const list = entries || [];
    return (
        <div style={{ ...C.overlay, ...(phone ? { padding: 0 } : {}) }} onClick={() => onClose && onClose()}>
            <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}
                style={{ ...C.modal, ...(phone ? C.modalPhone : {}) }}>
                <div style={C.modalHead}>
                    <span style={C.modalTitle}>{date.getMonth() + 1}월 {date.getDate()}일 ({WEEK_KO[date.getDay()]}) · {list.length}건</span>
                    <button ref={closeBtnRef} onClick={() => onClose && onClose()} style={C.closeBtn}>닫기</button>
                </div>
                <div style={C.modalBody}>
                    {list.length === 0 && <div style={C.emptyNote}>해당하는 건이 없습니다.</div>}
                    {list.map(e => (
                        <PatientCard key={e.key || e.o.id} entry={e} partnerNameOf={partnerNameOf} dueLabel={dueLabel} simple={simple} hideDesignChip={hideDesignChip}
                            onOpen={onOpen} onPreview={onPreview} actions={actionsOf ? actionsOf(e) : null} />
                    ))}
                    {foot && <div style={C.foot}>{foot}</div>}
                </div>
            </div>
        </div>
    );
}

// 카드 1장 = 환자 1명. 기본은 [의뢰서 열기]·[3D 디자인 확인]만 — 추가 조작은 actions(기공소만)
function PatientCard({ entry, partnerNameOf, dueLabel, onOpen, onPreview, actions, simple, hideDesignChip }) {
    const { o, st, done, color } = entry;
    const lines = teethLinesOf(o);
    const due = dueOf(o);
    const partner = partnerNameOf ? partnerNameOf(o) : '';
    const notes = (o.specialNotes || '').toString().trim();
    const stampAt = o.stageUpdatedAt || o.completedAt;   // updatedAt 폴백 없음 — 공정과 무관한 갱신(열람 등)을 공정 시각으로 보이지 않게
    const rc = o.isRemake ? remakeBadgeColors(o) : null;
    const label = (st && st.label) || '';
    const detail = st && st.detail;
    return (
        <div style={{ ...C.pcard, borderLeftColor: color, ...(done ? { opacity: 0.75 } : {}) }}>
            <div style={C.pcHead}>
                <span style={C.pcName}>{o.patientName || '환자명 없음'}</span>
                {partner && <span style={C.pcPartner}>{partner}</span>}
                {rc && <span style={{ ...C.pcBadge, background: rc.bg, color: rc.fg }}>{remakeLabelOf(o, '재제작')}</span>}
                {label && <span style={{ ...C.pcStage, color, borderColor: color + '66', background: color + '14' }}>{label}</span>}
            </div>
            <div style={C.pcSec}>
                <div style={C.pcSecTitle}>치식</div>
                {lines.length === 0
                    ? <div style={C.pcMuted}>치식 정보 없음</div>
                    : lines.map((l, i) => <div key={i} style={C.pcLine}>{l}</div>)}
            </div>
            <div style={C.pcSec}>
                <div style={C.pcSecTitle}>공정</div>
                <div style={C.pcLine}>
                    <span style={{ fontWeight: 700, color }}>{label || '-'}</span>
                    {detail && <span style={{ color: '#64748b' }}>{' · ' + detail}</span>}
                    {stampAt && <span style={{ color: '#94a3b8', fontSize: 11.5 }}>{' · 갱신 ' + fmtTime(stampAt)}</span>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                    {/* 보조 '디자인완료' 칩 — 2026-10-12 기공소 3단계(hideDesignChip)에서는 숨김: 공정 행 label(디자인 완료/포장 완료)과 겹친다. 치과 모드 제작 현황은 종전 그대로 */}
                    {!simple && !hideDesignChip && o.designDone === true && <span style={C.chipDesign}>디자인완료</span>}
                    {onPreview && (o.designPreview?.files || []).length > 0 && (
                        <button onClick={() => onPreview(o)} style={C.btn3d} title={simple ? '3D 디자인 미리보기' : '디자인 완료 3D 파일 확인 (디자인 확인용 — 최종 보철과 다를 수 있음)'}>{simple ? '3D 디자인 미리보기' : '3D 디자인 확인'}</button>
                    )}
                </div>
            </div>
            <div style={C.pcSec}>
                <div style={C.pcLine}><span style={C.pcKey}>{dueLabel}</span>{due ? `${due.getMonth() + 1}월 ${due.getDate()}일 (${WEEK_KO[due.getDay()]})` : '-'}</div>
                {notes && <div style={{ ...C.pcLine, ...C.pcNotes }}><span style={C.pcKey}>특이사항</span>{notes}</div>}
            </div>
            {actions && <div style={C.pcActions}>{actions}</div>}
            <div style={C.pcFoot}>
                <button onClick={() => onOpen && onOpen(o)} style={C.btnOpen}>의뢰서 열기</button>
            </div>
        </div>
    );
}

const C = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
    modal: { background: '#fff', borderRadius: 12, width: 'min(720px, 100%)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden' },
    modalPhone: { width: '100%', height: '100%', maxHeight: '100%', borderRadius: 0 },
    modalHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 },
    modalTitle: { fontSize: 15, fontWeight: 800, color: '#0f172a' },
    closeBtn: { padding: '7px 13px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
    modalBody: { overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 },
    emptyNote: { fontSize: 12.5, color: '#64748b', padding: 12, textAlign: 'center' },
    foot: { fontSize: 11.5, color: '#94a3b8', lineHeight: 1.5, padding: '2px 2px 0' },

    pcard: { border: '1px solid #e2e8f0', borderLeft: '4px solid', borderRadius: 10, padding: '12px 14px', background: '#fff' },
    pcHead: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
    pcName: { fontSize: 17, fontWeight: 800, color: '#0f172a' },
    pcPartner: { fontSize: 12.5, color: '#64748b', fontWeight: 600 },
    pcBadge: { fontSize: 10.5, fontWeight: 700, padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap' },
    pcStage: { marginLeft: 'auto', fontSize: 11.5, fontWeight: 800, padding: '3px 9px', borderRadius: 999, border: '1px solid', whiteSpace: 'nowrap' },
    pcSec: { marginTop: 6, paddingTop: 6, borderTop: '1px solid #f1f5f9' },
    pcSecTitle: { fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 3 },
    pcLine: { fontSize: 13, color: '#334155', lineHeight: 1.6 },
    pcMuted: { fontSize: 12.5, color: '#94a3b8' },
    pcKey: { display: 'inline-block', minWidth: 62, color: '#94a3b8', fontSize: 12, fontWeight: 700 },
    pcNotes: { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' },
    pcActions: { marginTop: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9' },
    pcFoot: { display: 'flex', justifyContent: 'flex-end', marginTop: 8 },
    chipDesign: { fontSize: 11, fontWeight: 700, color: '#4338ca', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 999, padding: '3px 9px' },
    btn3d: { padding: '3px 9px', borderRadius: 999, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 11, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
    btnOpen: { padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
};

export default ProcessDayModal;
